import React, { useState, useEffect, useCallback } from 'react';
import cx from 'classnames';

import Input from './input';
import Checkbox from './checkbox';
import Button from './button';
import Textarea from './textarea';

import { shipperUserStyles } from './styled';
import Icon from '@ontruck/ontruck-react-components/lib/Icon';
import { createShipperUser, updateShipperUser, fetchUser } from 'modules/shippers/http';
import { useHistory, useParams } from 'react-router-dom';
import {
  formatDataToCreateUser,
  formatDataToEditUser,
  validateFormFields,
} from 'modules/shippers/utils';
import { injectIntl } from 'react-intl';

import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';
import { useSelector } from 'react-redux';

const defaultValues = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  commercial_communications_accepted: false,
  always_cc: false,
  contact_instructions: '',
  uuid: '',
};

const ShipperUserForm = ({
  shipperId,
  mode,
  onUserOpened,
  onUserModified,
  onUserAdded,
  className,
  intl,
}) => {
  const [userValues, setUserValues] = useState(defaultValues);
  const [requestError, setRequestError] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [warnings, setWarnings] = useState({
    first_name: null,
    last_name: null,
    phone: null,
    email: null,
  });

  const { userId } = useParams();
  const history = useHistory();
  const isEditing = mode === 'EDIT';

  const t = intl.messages.modules.shippers['shipper-user-form'];

  const handleClose = useCallback(() => {
    history.push({
      pathname: `/shipper/${shipperId}`,
    });
  }, [history, shipperId]);

  const handleFieldChange = useCallback(
    (field) => {
      return (value) => {
        setUserValues((currentValues) => ({ ...(currentValues || {}), [field]: value }));
        setWarnings((currentValues) => ({ ...(currentValues || {}), [field]: null }));
      };
    },
    [setUserValues]
  );

  const onRequestSuccess = () => {
    setRequestSuccess(true);
    setTimeout(() => setRequestSuccess(false), 1500);
  };

  const onRequestError = () => {
    setRequestError(true);
    setTimeout(() => setRequestError(false), 2000);
  };

  const handleEditUser = useCallback(async () => {
    try {
      const updatedUser = await updateShipperUser(userValues?.id, formatDataToEditUser(userValues));
      onUserModified(updatedUser);
      onRequestSuccess();
      handleClose();
      onUserOpened(null);
    } catch (e) {
      onRequestError();
    }
  }, [userValues, onUserModified, handleClose, onUserOpened]);

  const handleCreateUser = useCallback(async () => {
    try {
      const warnings = validateFormFields(userValues);

      if (warnings) {
        setWarnings(warnings);
        return;
      } else {
        setWarnings({});
      }

      const createdUser = await createShipperUser(shipperId, formatDataToCreateUser(userValues));

      onUserAdded(createdUser);
      setUserValues(defaultValues);
      onRequestSuccess();
    } catch (e) {
      onRequestError();
    }
  }, [shipperId, userValues, onUserAdded]);

  useEffect(() => {
    (async function getUser() {
      try {
        setUserValues({});
        if (!userId) return setUserValues(defaultValues);

        const user = await fetchUser(userId);
        setUserValues(user);
        onUserOpened(userId);
      } catch (e) {
        setUserValues({});
        console.error(e);
      }
    })();
  }, [userId, onUserOpened]);

  const shippersMinimalisticForms = useSelector((state) =>
    getSpecificClientConfig(state, constants.SHIPPERS_MINIMALISTIC_FORMS)
  );

  return (
    <>
      {!!mode && <div style={{ width: '370px' }} />}
      <div className={cx(className, { open: !!mode })}>
        <div className="header">
          {isEditing ? t.titles.edit : t.titles.create}

          <span className="header__close" data-testid="close-icon" onClick={handleClose}>
            <Icon name="close" />
          </span>
        </div>
        <div className="content">
          <div className="content__title">{t.contact}</div>
          <Input
            name="name"
            placeholder={t.placeholders.name}
            onChange={handleFieldChange('first_name')}
            value={userValues?.first_name}
            warning={warnings.first_name}
          />
          <Input
            name="last_name"
            placeholder={t.placeholders.last_name}
            onChange={handleFieldChange('last_name')}
            value={userValues?.last_name}
            warning={warnings.last_name}
          />
          <Input
            name="email"
            placeholder={t.placeholders.email}
            onChange={handleFieldChange('email')}
            value={userValues?.email}
            warning={warnings.email}
          />
          <Input
            name="phone"
            placeholder={t.placeholders.phone}
            onChange={handleFieldChange('phone')}
            value={userValues?.phone}
            warning={warnings.phone}
          />
          <div className="content__title margin-top">{t.special_instructions}</div>
          <Checkbox
            onChange={handleFieldChange('always_cc')}
            value={userValues?.always_cc}
            text={t.always_cc}
            infoText={t.always_cc_info}
            width="100px"
          />
          <div className="subtitle">{t.instructions}</div>
          <Textarea
            placeholder={t.textarea_placeholder}
            className="textarea"
            onChange={handleFieldChange('contact_instructions')}
            value={userValues?.contact_instructions}
          />

          {!shippersMinimalisticForms && (
            <>
              <div className="content__title margin-top">{t.gdpr}</div>
              <Checkbox
                onChange={handleFieldChange('commercial_communications_accepted')}
                text={t.gdpr_text}
                width="239px"
                value={userValues?.commercial_communications_accepted}
              />
            </>
          )}
          {!shippersMinimalisticForms && (
            <>
              <div className="content__title margin-top">{t.uuid}</div>
              <p>{userValues.uuid}</p>
            </>
          )}
        </div>
        <Button
          error={requestError}
          errorText={t.error}
          successText={t.success}
          success={requestSuccess}
          text={isEditing ? t.save : t.add}
          onClick={isEditing ? handleEditUser : handleCreateUser}
        />
      </div>
    </>
  );
};

export default injectIntl(shipperUserStyles(ShipperUserForm));
